import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, subtask, type Subtask } from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

/**
 * The `UPDATE ... WHERE id = ?` in this file use `patchRowVerified`: an
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). These mutations **can throw**.
 */

export async function createSubtaskMutation(
  db: PowerSyncDrizzleDB,
  todoId: string,
  text: string,
  currentSubtasks: readonly Subtask[],
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  let maxOrder = 0;
  for (const s of currentSubtasks) {
    maxOrder = Math.max(maxOrder, s.order ?? 0);
  }
  await db.insert(subtask).values({
    id,
    todo_id: todoId,
    text,
    completed: 0,
    order: maxOrder + 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSubtaskMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ text: string; completed: number; order: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, subtask, subtask.id, id, updates, options);
}

export async function toggleSubtaskMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentCompleted: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    subtask,
    subtask.id,
    id,
    { completed: currentCompleted ? 0 : 1 },
    options,
  );
}

export async function deleteSubtaskMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(subtask).where(eq(subtask.id, id));
}

export async function moveSubtaskMutation(
  db: PowerSyncDrizzleDB,
  subtasks: readonly Subtask[],
  id: string,
  direction: "up" | "down",
  options?: VerifiedWriteOptions,
): Promise<void> {
  const index = subtasks.findIndex((s) => s.id === id);
  if (index === -1) {
    return;
  }
  if (direction === "up" && index === 0) {
    return;
  }
  if (direction === "down" && index === subtasks.length - 1) {
    return;
  }

  const otherIndex = direction === "up" ? index - 1 : index + 1;
  const me = subtasks[index];
  const other = subtasks[otherIndex];
  if (!me || !other) {
    return;
  }

  const now = nowISO();

  // Order swap is a transaction: cannot retry
  // internally. If one of two rows is not in view, its UPDATE matches
  // nothing and other matches -> two steps with SAME order. Awaited outside.
  await waitForRowPresent(db, subtask, subtask.id, me.id, options);
  await waitForRowPresent(db, subtask, subtask.id, other.id, options);

  await db.transaction(async (trx) => {
    await trx
      .update(subtask)
      .set({ order: other.order, updated_at: now })
      .where(eq(subtask.id, me.id));
    await trx
      .update(subtask)
      .set({ order: me.order, updated_at: now })
      .where(eq(subtask.id, other.id));
  });
}
